<template>
  <div>
    <portal to="entry_header">
      <h1>Competitor Institute Account</h1>
    </portal>
    <p>To submit an entry on someone's behalf, please enter their email address. If they already have an Institute account, the entry will be linked to them otherwise a free account will be created for them.</p>

    <form-wrapper @submit="submit">
      <div v-if="!competitor">
        <input-row
          v-model="$v.email.$model"
          label="Competitor's email address:"
          type="email"
          :error-message="emailError"
        >
          <template #label>
            <a
              href="#"
              @click.prevent="useAccountEmail"
            >
              Use my email address
            </a>
          </template>
        </input-row>
        <p>If the competitor does not have their own email address, you can use yours to receive account-related updates directly.</p>

        <div
          v-if="matchingAccounts.length > 0 && value === 0"
          class="uk-width-1-1"
        >
          <p><strong>We found these accounts using this email address:</strong></p>

          <div
            v-for="user in matchingAccounts"
            :key="user.data.id"
            class="uk-button uk-margin-bottom uk-width-1-1"
            @click="selectAccount(user.data.id)"
          >
            <span v-if="registrar.data.id === user.data.id">
              Assign to yourself ({{ user.data.user_name }})
            </span>
            <span v-else>
              Assign to <strong>{{ user.data.full_name }}</strong> ({{ user.data.user_name }})
            </span>
          </div>

          <div
            v-if="canCreateDuplicateAccount"
            class="uk-button uk-button-primary uk-margin-bottom uk-width-1-1"
            @click="confirmCreateAccount"
          >
            <strong v-html="createAccountText" />
          </div>
        </div>

        <div
          v-if="newUser && newUser.errors"
          class="uk-width-1-1"
        >
          <ul class="uk-text-danger uk-margin-left">
            <li
              v-for="(error, key) in newUser.errors"
              :key="key"
            >
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
      <div v-if="competitor">
        <div class="uk-alert uk-alert-large">
          <a
            v-if="!disabled"
            class="uk-alert-close uk-close"
            title="Remove competitor"
            @click.prevent="selectAccount(0)"
          />

          <h3 class="uk-margin-bottom">
            This entry will be linked to the following account:
          </h3>
          <ul>
            <li>Full Name: {{ competitor.data.full_name }}</li>
            <li>Username: {{ competitor.data.user_name }}</li>
            <li v-if="competitor.data.email_address">
              Email: {{ competitor.data.email_address }}
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="value === 0 && matchingAccounts.length === 0"
        class="uk-text-right uk-width-1-1"
      >
        <primary-button
          class="uk-width-1-1"
          :text="buttonText"
        />
      </div>
    </form-wrapper>

    <Confirm
      v-if="showCreateConfirmation"
      confirm-text="Yes, create this account"
      @cancel="cancelCreateAccount"
      @confirm="createAccount"
    >
      <template #default>
        <p>
          You are about to create a free Institute account for the competitor <strong>{{ studentDetails.first_name }} {{ studentDetails.last_name }}</strong>
          using the email address <i>{{ email }}</i>. Do you want to continue?
        </p>
      </template>
    </Confirm>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from "vue-property-decorator";
import formWrapper from "../form/form-wrapper.vue";
import inputRow from "../form/input-row.vue";
import primaryButton from "../form/primary-button.vue";
import { required, email } from "vuelidate/lib/validators";
import FormStep from "./FormStep";
import HouseholdModel, { create as createHousehold } from "../../models/HouseholdModel";
import UserModel, { create as createUser, UserModelData } from "../../models/UserModel";
import EventEntryModel from "../../models/EventEntryModel";
import HouseholdMemberModel from "../../models/HouseholdMemberModel";
import Confirm from '../misc/ConfirmationModal.vue';

@Component({
  components: {
    'form-wrapper': formWrapper,
    'input-row': inputRow,
    'primary-button': primaryButton,
    Confirm
  },
  validations: {
    email: {
      required,
      email
    },
  }
})
export default class AuthenticateCompetitor extends FormStep {
  @Prop() readonly studentDetails!: StudentDetails;
  @Prop() readonly addressDetails!: AddressDetails;
  @Prop() readonly entry!: EventEntryModel;

  @Prop() readonly value!: number;

  email = '';

  saving = false;

  newUser: UserModel = createUser();
  showCreateConfirmation = false;

  created(): void {
    this.$store.dispatch('households/fetchOwned');
  }

  get createAccountText(): string {
    if (this.saving) {
      return '<span class="uk-icon-spin uk-icon-spinner"></span> Creating account...';
    }

    return 'Create a new account';
  }

  get canCreateDuplicateAccount(): boolean {
    return this.email === this.registrar.data.email_address;
  }

  get buttonText(): string {
    if (this.saving) {
      return '<span class="uk-icon-spin uk-icon-spinner"></span> Saving...';
    }

    if (this.competitor) {
      return 'Assign to this user';
    }

    return '<span class="uk-icon-search"></span> Search for an existing Institute account to link this entry to.';
  }

  get emailError(): string {
    return this.$v.email.$invalid && this.$v.email.$dirty ? 'A valid email is required.' : '';
  }

  get registrar(): UserModel {
    return this.$store.getters['context/user'];
  }

  get competitor(): UserModel | null {
    if (this.newUser.data.id && this.newUser.data.id === this.value) {
      return this.newUser;
    }

    if (this.entry.competitor && this.value === this.entry.competitor.data.id) {
      return this.entry.competitor;
    }

    if (this.value > 0) {
      if (this.matchingAccounts.length > 0) {
        const matches = this.matchingAccounts.filter((u: UserModel) => u.data.id === this.value);
        if (matches.length > 0) {
          return matches[0];
        }
      }

      const user = this.$store.getters['users/getById'](this.value);
      if (user) {
        return user;
      }
    }

    return null;
  }

  get household(): HouseholdModel {
    // Just grab the first household for now.
    return this.$store.getters['households/owned'](this.$store.getters['context/user'].data.id)[0];
  }

  get matchingAccounts(): UserModel[] {
    return this.$store.getters['users/searchResults'](this.email).filter((a: UserModel) => {
      return a.data.id !== this.registrar.data.id;
    });
  }

  confirmCreateAccount(): void {
    this.showCreateConfirmation = true;
  }

  cancelCreateAccount(): void {
    this.showCreateConfirmation = false;
    this.saving = false;
  }

  createAccount(): void {
    this.showCreateConfirmation = false;

    this.saving = true;

    let hPromise;
    if (this.household) {
      hPromise = Promise.resolve();
    } else {
      const household = createHousehold();
      household.data.type = this.entry.data.registrar_role === 'guardian' ? 'family' : 'school';
      hPromise = this.$store.dispatch('households/create', household)
    }

    hPromise.then(() => {
      this.newUser = createUser({
        first_name: this.studentDetails.first_name,
        last_name: this.studentDetails.last_name,
        dan_id: this.studentDetails.dan_id,
        gup_id: this.studentDetails.gup_id,
        gender: this.studentDetails.gender,
        birthday: this.studentDetails.dob,
        zipcode: this.addressDetails.zipcode,
        city: this.addressDetails.city,
        household_id: this.household.data.id,
        email_address: this.email,
      } as UserModelData);

      this.$store.dispatch('users/create', this.newUser).then((newUser: UserModel) => {
        this.newUser = newUser;
        if (this.newUser.data.id) {
          this.selectAccount(this.newUser.data.id);
        }
      }).catch((err) => {
        this.newUser = err;
      }).finally(() => {
        this.saving = false;
      });
    });
  }

  submit(): void {
    this.$v.$touch();

    if (this.$v.$invalid) {
      return;
    }

    this.saving = true;
    this.processSubmit();
  }

  processSubmit(): void {
    if (!this.value) {
      this.$store.dispatch('users/searchByEmail', this.email).then((term: string) => {
        if (this.matchingAccounts.length === 0) {
          this.showCreateConfirmation = true;
        } else {
          this.saving = false;
        }
      });
    }
  }

  selectAccount(userId: number): void {
    this.$emit('input', userId);
    this.$emit('valid', userId > 0);
  }

  useAccountEmail(): void {
    this.email = this.registrar.data.email_address;
  }

  runValidation(): void {
    this.$emit('valid', this.value > 0);
  }

  watchFormChanges(): void {
    // We don't want to automatically watch the form as it's not part of the entry data.
  }
}
</script>

<style scoped>

</style>
